import {
  Box,
  FlexBox,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import {
  FlowMessengerActionSubflowReturnPayload,
  SubflowMessengerAction,
} from 'lib-shared-types-api';
import { useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import {
  HookFormSelectionListSingle,
  HookFormSwitch,
  HookFormText,
} from 'components/hookForm';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import {
  FlowMessengerExtraData,
  FlowMessengerNodeSubflowReturn,
} from '../../../flow.types';
import { findUniqueExitPathFromSubflow } from '../../shared/subflows/findUniqueExitPathFromSubflow';

import styles from './subflowReturnModal.module.scss';

interface FormValues {
  label: string;
  shouldReuseExistingSubflow: boolean;
  existingReturnToMainFlow: string | undefined;
}

export function SubflowReturnModal({
  node,
  nodes,
  replaceNode,
  triggerClose,
}: FlowManagerEditModalComponentT<
  FlowMessengerNodeSubflowReturn,
  FlowMessengerExtraData
>): JSX.Element | null {
  const editMode = node.payload?.returnId !== undefined;

  // The returnId and the node.id are the same ONLY for the original node that has defined the returnId in question.
  const isAnOriginalReturnNode = node.payload?.returnId === node.id;

  const reuseReturnNodeMode = editMode && !isAnOriginalReturnNode;

  const uniqueReturnToMainFlowsList = findUniqueExitPathFromSubflow(
    Object.values(nodes) as SubflowMessengerAction[],
  );

  const hasExistingReturnToMainFlows = uniqueReturnToMainFlowsList.length > 0;

  const getReturnNodeLabelByReturnId = (
    returnId: string | undefined,
  ): string => {
    const returnToMainFlow = uniqueReturnToMainFlowsList.find(
      ({ payload }) => payload.returnId === returnId,
    );
    return returnToMainFlow?.payload?.label || 'Unknown subflow';
  };

  const makeNextOriginalReturnNodeLabel = useCallback((): string => {
    const nextIndex = uniqueReturnToMainFlowsList.length + 1;
    return `Exit ${nextIndex}`;
  }, [uniqueReturnToMainFlowsList]);

  const getDefaultLabel = (): string => {
    if (editMode) {
      if (isAnOriginalReturnNode) {
        return node.payload?.label || '';
      }

      // If the returnId is different from the node.id, it means that the node is re-using a returnId from another node.
      if (node.payload?.returnId) {
        return getReturnNodeLabelByReturnId(node.payload.returnId);
      }

      return '';
    }

    return makeNextOriginalReturnNodeLabel();
  };

  const { control, formState, handleSubmit, setValue } = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      label: getDefaultLabel(),
      existingReturnToMainFlow: node.payload?.returnId || undefined,
      shouldReuseExistingSubflow: reuseReturnNodeMode,
    },
  });

  const shouldReuseExistingSubflowValue = useWatch({
    control,
    name: 'shouldReuseExistingSubflow',
  });

  const updateNodePayload = (
    payload: FlowMessengerActionSubflowReturnPayload,
  ): FlowMessengerNodeSubflowReturn => ({
    ...node,
    nodeType: FlowNodeTypes.SUBFLOW_RETURN,
    isAction: false,
    isRemovable: true,
    isEditable: true,
    text: '',
    payload,
    badges: [],
    overlay: [],
  });

  const updateReturnNodeLabelsWithSameReturnId = (values: FormValues) => {
    const nodesToUpdate = Object.values(nodes).filter(
      (node) =>
        node.nodeType === FlowNodeTypes.SUBFLOW_RETURN &&
        node.payload.returnId === values.existingReturnToMainFlow,
    ) as FlowMessengerNodeSubflowReturn[];

    nodesToUpdate.forEach((nodeToUpdate) => {
      replaceNode({
        ...nodeToUpdate,
        payload: {
          ...nodeToUpdate.payload,
          label: values.label,
        },
      });
    });
  };

  const handleSaveClick = (formValues: FormValues) => {
    if (
      formValues.shouldReuseExistingSubflow &&
      formValues.existingReturnToMainFlow
    ) {
      const label = getReturnNodeLabelByReturnId(
        formValues.existingReturnToMainFlow,
      );

      /*
       * To distinguish between nodes that are re-using a return ID and the original node which has defined the return ID, the node.id is used as the returnId for the original node. otherwise, the returnId is the same as the selected node and will NOT be the same as the node.id
       */
      replaceNode(
        updateNodePayload({
          label,
          returnId: formValues.existingReturnToMainFlow,
        }),
      );

      ampli.subflowBackToMainFlowNodeReused();
    } else {
      replaceNode(
        updateNodePayload({ label: formValues.label, returnId: node.id }),
      );

      ampli.subflowBackToMainFlowNodeUsed();
    }

    if (
      node.payload?.label !== undefined &&
      node.payload?.label !== formValues.label
    ) {
      // label changed from a defined value to another defined value => need to update all labels with the same returnId
      updateReturnNodeLabelsWithSameReturnId(formValues);
    }

    triggerClose();
  };

  return (
    <Box width="500px">
      <ModalHead title="Back to main flow" onClose={triggerClose} />

      <ModalBody>
        <FlexBox flexDirection="column" gap="400">
          <form>
            <FormGrid>
              <FormField>
                Use &quot;Back to main flow&quot; to link your sub-flow with the
                main flow&apos;s nodes.
              </FormField>

              {hasExistingReturnToMainFlows && (
                <FormField>
                  <HookFormSwitch
                    control={control}
                    className={styles.wrapper}
                    name="shouldReuseExistingSubflow"
                    label="Re-use an existing “Back to main flow” node"
                    componentProps={{
                      onChange: (event) => {
                        if (!event.target.checked) {
                          setValue('label', makeNextOriginalReturnNodeLabel());
                          setValue('existingReturnToMainFlow', undefined);
                        }
                      },
                    }}
                  />
                </FormField>
              )}

              {hasExistingReturnToMainFlows && (
                <FormField>
                  By re-using an existing “Back to main flow” node you will
                  reduce the amount of exit paths in the sub-flow.
                </FormField>
              )}

              <FormField>
                {shouldReuseExistingSubflowValue ? (
                  <HookFormSelectionListSingle
                    control={control}
                    name="existingReturnToMainFlow"
                    label="Select existing node"
                    componentProps={{
                      getItemTitleByValue(returnIdValue?: string | null) {
                        return (
                          uniqueReturnToMainFlowsList.find(
                            ({ payload }) => payload.returnId === returnIdValue,
                          )?.payload.label || 'Unknown item'
                        );
                      },
                    }}
                  >
                    {uniqueReturnToMainFlowsList.map((returnToMainFlow) => (
                      <SelectionList.Item
                        key={returnToMainFlow.id}
                        value={returnToMainFlow.payload.returnId}
                      >
                        <Text.Paragraph>
                          {returnToMainFlow.payload.label}
                        </Text.Paragraph>
                      </SelectionList.Item>
                    ))}
                  </HookFormSelectionListSingle>
                ) : (
                  <HookFormText
                    name="label"
                    label="Exit name"
                    control={control}
                    componentProps={{
                      autoFocus: true,
                      placeholder: 'Exit 1, Main exit, etc...',
                    }}
                  />
                )}
              </FormField>
            </FormGrid>
          </form>
        </FlexBox>
      </ModalBody>

      <ModalFoot
        confirmOnClick={handleSubmit(handleSaveClick)}
        confirmName="Save"
        confirmProps={{
          isLoading: formState.isSubmitting,
          loadingIndicator: 'Saving...',
        }}
        cancelOnClick={triggerClose}
      />
    </Box>
  );
}
